import { PUBLIC_SITE_URL } from '../config/api';

/**
 * Feuille imprimable — le style des guides utilisateurs, appliqué aux
 * documents d'exploitation.
 *
 * Pourquoi une page HTML et non un dessin jsPDF : la charte s'exprime en CSS
 * (Anton, filet de dégradé, hiérarchie), et le navigateur sait déjà paginer.
 * Dessiner tout ça au millimètre en jsPDF demandait des dizaines de lignes de
 * coordonnées pour un rendu plus pauvre.
 *
 * Le point que les guides ont résolu avant nous : **sombre à l'écran, blanc au
 * papier**. Un aperçu à la charte, une impression qui ne vide pas une cartouche
 * et qui reste lisible sur la ligne de départ. Les variables CSS basculent dans
 * `@media print`, rien d'autre ne change.
 */

const CSS = `
:root{--bg:#0b0b0b;--fg:#f5f5f5;--muted:#9a9a9a;--line:rgba(255,255,255,.14);
      --grad:linear-gradient(90deg,#ef4444,#f97316,#facc15);--band:rgba(255,255,255,.05)}
*{box-sizing:border-box}
body{margin:0;background:var(--bg);color:var(--fg);
     font-family:Inter,system-ui,-apple-system,sans-serif;line-height:1.45}
.wrap{max-width:960px;margin:0 auto;padding:28px 24px 48px}

/* En-tête : le filet de dégradé est la seule marque de couleur — la charte le
   réserve aux accents, et sur du papier un aplat coûterait une cartouche. */
header{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;
       padding-bottom:10px;border-bottom:2px solid transparent;
       border-image:var(--grad) 1}
h1{font-family:Anton,Impact,sans-serif;font-weight:400;font-size:30px;
   letter-spacing:.02em;text-transform:uppercase;margin:0}
.sub{color:var(--muted);font-size:12.5px;margin-top:3px}
header img{height:26px;width:auto;opacity:.95}

h2{font-family:Anton,Impact,sans-serif;font-weight:400;font-size:17px;
   letter-spacing:.04em;text-transform:uppercase;margin:26px 0 8px;
   padding-bottom:5px;border-bottom:1px solid var(--line)}
h2 small{font-family:Inter;font-size:11.5px;color:var(--muted);
         text-transform:none;letter-spacing:0;margin-left:9px}

table{width:100%;border-collapse:collapse;font-size:12px}
th{text-align:left;font-size:9.5px;letter-spacing:.07em;text-transform:uppercase;
   color:var(--muted);font-weight:700;padding:5px 8px;border-bottom:1px solid var(--line)}
td{padding:5px 8px;border-bottom:1px solid var(--line);vertical-align:top}
/* Grille : les couloirs en colonnes. Un filet vertical léger, sinon l'œil
   glisse d'une colonne à l'autre — surtout avec des blocs de hauteurs
   différentes. */
table.grid th,table.grid td{border-right:1px solid var(--line)}
/* Couloirs de largeur égale (2026-09-28) : en disposition automatique, une
   colonne s'élargissait pour un nom long et rétrécissait pour un court, et la
   grille affichée au mur perdait son alignement. Seule la colonne qui porte
   une largeur (l'heure) la garde ; un mot trop long se coupe. */
table.grid{table-layout:fixed}
table.grid td{overflow-wrap:anywhere}
table.grid th:last-child,table.grid td:last-child{border-right:0}
table.grid td{text-align:left}
tr.band td{background:var(--band);font-family:Anton,Impact,sans-serif;
           font-size:13px;letter-spacing:.03em;text-transform:uppercase}
.bib{font-variant-numeric:tabular-nums;font-weight:700;white-space:nowrap}
.hour{font-variant-numeric:tabular-nums;font-weight:700;white-space:nowrap}
.hour .sub2{font-weight:400}
/* Sous-titre d'un bandeau (la catégorie sous le WOD) : discret, il découpe
   sans se répéter dans chaque case. */
tr.subband td{color:var(--muted);font-size:11px;font-weight:600;padding-top:7px}
.dim{color:var(--muted)}
/* Seconde ligne d'une cellule : les athlètes sous le nom d'équipe. Plus petit
   et gris — c'est un complément, pas un second titre. */
.sub2{display:block;color:var(--muted);font-size:10px;line-height:1.3;margin-top:1px}
.empty{color:var(--muted);font-style:italic}
tbody tr{page-break-inside:avoid}
.foot{margin-top:26px;color:var(--muted);font-size:10.5px;text-align:center}

/* Bandeau d'aperçu : disparaît à l'impression, il n'appartient pas au document. */
.bar{position:sticky;top:0;z-index:5;display:flex;gap:10px;align-items:center;
     justify-content:flex-end;padding:10px 24px;background:rgba(11,11,11,.92);
     border-bottom:1px solid var(--line);backdrop-filter:blur(6px)}
/* Boutons de la charte (btn-glass de cloud-admin, recopié : la page est un
   document autonome, elle n'a pas la feuille de style de l'app). Plaque
   sombre, anneau peint sur le contour, texte blanc : le dégradé ne se pose
   jamais en aplat sous du texte noir. */
.bar button{position:relative;isolation:isolate;overflow:hidden;font:inherit;
            font-size:12.5px;font-weight:600;padding:7px 14px;border-radius:3px;border:0;
            background:rgba(15,20,30,.85);color:#f1f4f9;cursor:pointer;
            transition:transform .18s cubic-bezier(.2,.8,.2,1),box-shadow .18s ease;
            box-shadow:inset 0 1px 0 rgba(255,255,255,.18),0 0 18px -3px rgba(255,255,255,.10)}
.bar button::before{content:"";position:absolute;inset:0;border-radius:inherit;padding:1.5px;
            background:linear-gradient(rgba(255,255,255,.28),rgba(255,255,255,.08));
            -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
            -webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;z-index:-1}
.bar button.go{color:#fff;
            box-shadow:inset 0 1px 0 rgba(255,255,255,.22),0 0 22px -2px rgba(249,115,22,.20)}
.bar button.go::before{padding:2px;background:linear-gradient(120deg,#EF4444,#F97316 45%,#FACC15)}
@media (hover:hover) and (pointer:fine){.bar button:hover{transform:translateY(-1px)}}
.bar button:active{transform:translateY(0) scale(.97);transition-duration:.1s}

@media print{
  /* Le complément doit rester lisible au papier sans manger la ligne. */
  @page{size:A4;margin:11mm 12mm}
  /* Papier : fond blanc. Le sombre est pour l'écran. */
  :root{--bg:#fff;--fg:#111;--muted:#555;--line:#dcdcdc;--band:#f4f4f4}
  body{background:#fff;color:#111}
  .wrap{max-width:none;padding:0}
  .bar{display:none}
  header img{opacity:1}
  h1{font-size:23px}
  h2{font-size:14px;margin:14px 0 6px}
  table{font-size:10.5px}
  th{font-size:8.5px}
  td{padding:3.5px 7px}
  tr.band td{font-size:11.5px}
  tr.subband td{font-size:9.5px}
  .sub2{font-size:8.5px}
  thead{display:table-header-group}   /* l'en-tête se répète à chaque page */
}
`;

export interface SheetColumn {
  /** Libellé de colonne (déjà traduit). */
  label: string;
  /** Largeur CSS optionnelle — utile pour figer un dossard ou une heure. */
  width?: string;
  /** Classe de cellule : `bib`, `hour`, `dim`… */
  cls?: string;
}

/**
 * Une cellule. Le texte simple suffit dans la plupart des cas ; la forme
 * détaillée sert aux deux besoins de l'impression en grille :
 *   · `rowSpan` — un juge qui tient plusieurs heats d'affilée est UN bloc, pas
 *     son nom répété quatre fois ;
 *   · `sub` — les athlètes sous le nom d'équipe.
 */
export type SheetCell = string | {
  v: string; sub?: string; rowSpan?: number; cls?: string;
};

export interface SheetRow {
  /** Ligne de séparation pleine largeur (un WOD, une catégorie, un jour). */
  band?: string;
  /** Sous-titre pleine largeur, plus discret qu'un bandeau (la catégorie sous le WOD). */
  subBand?: string;
  cells?: SheetCell[];
}

export interface SheetOptions {
  title: string;
  /**
   * Paysage. Une grille de couloirs en colonnes ne tient pas en portrait ; en
   * paysage elle redevient lisible, et c'est la forme d'une vraie start list.
   */
  landscape?: boolean;
  subtitle?: string;
  /** Sections successives, chacune avec son titre et son tableau. */
  sections: { title?: string; note?: string; columns: SheetColumn[]; rows: SheetRow[];
              /** Filets verticaux entre colonnes (grille de couloirs). */
              grid?: boolean }[];
  footer?: string;
  /** Libellés du bandeau d'aperçu (traduits par l'appelant). */
  printLabel: string;
  closeLabel: string;
}

const esc = (s: string) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Ouvre la feuille dans un onglet et déclenche l'aperçu d'impression.
 *
 * Un onglet plutôt qu'un `<iframe>` caché : l'organisateur voit ce qu'il va
 * imprimer, peut choisir sa page, et refermer sans rien produire.
 */
export function openPrintableSheet(o: SheetOptions): void {
  const sections = o.sections.map((s) => `
    ${s.title ? `<h2>${esc(s.title)}${s.note ? `<small>${esc(s.note)}</small>` : ''}</h2>` : ''}
    <table${s.grid ? ' class="grid"' : ''}>
      <thead><tr>${s.columns.map((c) =>
        `<th${c.width ? ` style="width:${c.width}"` : ''}>${esc(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${s.rows.map((r) => r.band !== undefined
        ? `<tr class="band"><td colspan="${s.columns.length}">${esc(r.band)}</td></tr>`
        : r.subBand !== undefined
        ? `<tr class="subband"><td colspan="${s.columns.length}">${esc(r.subBand)}</td></tr>`
        : `<tr>${(r.cells || []).map((c, i) => {
            const d = typeof c === 'string' ? { v: c } as { v: string; sub?: string; rowSpan?: number; cls?: string } : c;
            const cls = d.cls ?? s.columns[i]?.cls;
            return `<td${cls ? ` class="${cls}"` : ''}`
              + `${d.rowSpan && d.rowSpan > 1 ? ` rowspan="${d.rowSpan}"` : ''}>`
              + esc(d.v)
              + (d.sub ? `<span class="sub2">${esc(d.sub)}</span>` : '')
              + '</td>';
          }).join('')}</tr>`).join('')}
      </tbody>
    </table>`).join('');

  const orientation = o.landscape
    ? '@media print{@page{size:A4 landscape;margin:10mm 12mm}}' : '';

  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>${esc(o.title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>${CSS}${orientation}</style></head><body>
<div class="bar">
  <button class="go" onclick="window.print()">${esc(o.printLabel)}</button>
  <button onclick="window.close()">${esc(o.closeLabel)}</button>
</div>
<div class="wrap">
  <header>
    <div><h1>${esc(o.title)}</h1>${o.subtitle ? `<div class="sub">${esc(o.subtitle)}</div>` : ''}</div>
    <img src="${PUBLIC_SITE_URL}/logos/LOGFIT-LOGO-ORANGE-PATH.svg" alt="LogFit">
  </header>
  ${sections}
  ${o.footer ? `<div class="foot">${esc(o.footer)}</div>` : ''}
</div>
<script>
  // On attend les polices : sans ça l'aperçu se dessine en Helvetica et le
  // document imprimé n'est pas celui qu'on a validé à l'écran.
  (document.fonts ? document.fonts.ready : Promise.resolve())
    .then(() => setTimeout(() => window.print(), 120));
</script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) return;                      // bloqueur de pop-up : rien de cassé
  w.document.write(html);
  w.document.close();
}
